import _ from 'lodash'
import {
    CreateExtArgs,
    CreateExtensionArgument,
    Extension,
    ExtensionAPI,
    type InitializeExtArgs,
    pointerUtils,
    Transaction
} from '@wix/document-manager-core'
import {APP_STATUS, DATA_TYPES, MASTER_PAGE_ID, RESOURCE_TYPES} from '../constants/constants'
import type {
    AppData,
    AppDefinitionId,
    AppInstallState,
    ApplicationId,
    AppsInstallStateMap,
    AppStateChangedCallback,
    ClientSpecMap,
    ClientSpecMapEntry,
    EditorClientSpecMapEntry,
    Experiment,
    Pointer
} from '@wix/document-services-types'
import {deepClone} from '@wix/wix-immutable-proxy'
import * as tpaUtils from './tpa/installedTpaAppsUtils'
import type {DataModelExtensionAPI} from './dataModel/dataModel'
import type {SchemaExtensionAPI} from './schema/schema'
import type {RMApi} from './rendererModel'
import type {PlatformExtensionAPI} from './platform'
import type {FragmentsExtensionAPI} from './fragments/fragments'
import type {BranchExtensionAPI} from './branches'
import type {DocumentServicesModelExtApi} from './documentServicesModel'
const {getPointer, getInnerPointer} = pointerUtils

const {INSTALLED, REMOVED} = APP_STATUS

export interface AppsInstallStateAPI extends ExtensionAPI {
    isAppInstalled(appDefId: AppDefinitionId | undefined): boolean
    isAppRemoved(appDefId: AppDefinitionId): boolean
    setAppInstalled(appDefId: AppDefinitionId, source?: string): void
    setAppUninstalled(appDefId: AppDefinitionId, source?: string): void
    getAppInstallState(appDefId: AppDefinitionId): AppInstallState | undefined
    getAllAppsInstallStatus(): AppsInstallStateMap
    getInstallStateByAppData(app: ClientSpecMapEntry, appsWithComps?: Set<AppDefinitionId>): AppInstallState | undefined
    getInstalledApps(): AppDefinitionId[]
    setAppInstallStateByAppData(app: EditorClientSpecMapEntry): void
    reportStateDifferenceByActions(
        clientSpecMap: ClientSpecMap,
        actions: {type: string; applicationId: ApplicationId}[],
        source: string
    ): void
    addStateToDataModel(newStateMap: AppsInstallStateMap): void
    fixAppStatus(appDefId: AppDefinitionId, source?: string): void
    fixAppsStatus(): void
    asyncFixAppsStatus(): Promise<void>
    getDiffBetweenSpecAndAppState(): string[]
    subscribeToStateChanges(listener: AppStateChangedCallback): void
    getStateMapPointerFromMasterPage(): Pointer
    migrateAppsStateToFragment(): void
}
export type AppsInstallStateExtensionAPI = ExtensionAPI & {
    appsInstallState: AppsInstallStateAPI
}
const APPS_INSTALL_STATE_ID = 'APPS_INSTALL_STATE'
const EVENTS = {
    APPS_INSTALL_STATE: {
        UPDATE: 'APPS_INSTALL_STATE_UPDATE'
    }
}

const shouldGetFromFragment = (experimentInstance: Experiment) => {
    return !!(experimentInstance.isOpen('dm_fragments') && experimentInstance.isOpen('dm_fragmentsGetApps'))
}

const getAppsInstallStatePointerFromFragment = () =>
    getPointer(APPS_INSTALL_STATE_ID, DATA_TYPES.data, {
        fragmentType: RESOURCE_TYPES.APP_STATE,
        fragmentInstance: `${RESOURCE_TYPES.APP_STATE}Singleton`
    })

const getAppsInstallStatePointerFromMasterPage = () => getPointer(APPS_INSTALL_STATE_ID, DATA_TYPES.data)

const getAppsInstallStatePointer = (getFromFragment: boolean = false) =>
    getFromFragment ? getAppsInstallStatePointerFromFragment() : getAppsInstallStatePointerFromMasterPage()

const getStateMapPointer = (getFromFragment: boolean) =>
    getInnerPointer(getAppsInstallStatePointer(getFromFragment), ['stateMap'])

const getStateMapPointerFromMasterPage = () => getInnerPointer(getAppsInstallStatePointerFromMasterPage(), ['stateMap'])

const getStateMapPointerFromFragment = () => getInnerPointer(getAppsInstallStatePointerFromFragment(), ['stateMap'])

const installedAppsFromStateMap = (stateMap: AppsInstallStateMap) =>
    _.reduce(
        stateMap,
        (installedAppDefIds, state, appDefId) => {
            if (state.status === INSTALLED) {
                installedAppDefIds.push(appDefId)
            }
            return installedAppDefIds
        },
        [] as AppDefinitionId[]
    )

const createExtension = ({experimentInstance}: CreateExtensionArgument): Extension => {
    let localAppsInstallState: AppsInstallStateMap = {}
    const stateChangeListeners: AppStateChangedCallback[] = []

    const createExtensionAPI = (createExtArgs: CreateExtArgs): AppsInstallStateExtAPI => {
        const {coreConfig, dal, pointers, eventEmitter, extensionAPI} = createExtArgs
        const {dataModel} = extensionAPI as DataModelExtensionAPI
        const {schemaAPI} = extensionAPI as SchemaExtensionAPI
        const {rendererModel} = extensionAPI as RMApi
        const {platform} = extensionAPI as PlatformExtensionAPI
        const {branches} = extensionAPI as BranchExtensionAPI
        const {documentServicesModel} = extensionAPI as DocumentServicesModelExtApi

        const addStateToDataModel = (newStateMap: AppsInstallStateMap) => {
            const masterPage = dataModel.getItem(MASTER_PAGE_ID, DATA_TYPES.data, MASTER_PAGE_ID)
            const dataItem = schemaAPI.createItemAccordingToSchema('AppsInstallState', DATA_TYPES.data, {
                stateMap: newStateMap,
                id: APPS_INSTALL_STATE_ID
            })
            if (masterPage?.appsInstallState?.id && !masterPage.appsInstallState.id.includes(APPS_INSTALL_STATE_ID)) {
                dal.remove(getPointer(masterPage.appsInstallState.id.replace(/^#/, ''), DATA_TYPES.data))
                dal.remove(getPointer(MASTER_PAGE_ID, DATA_TYPES.data, {innerPath: 'appsInstallState'}))
            }
            masterPage.appsInstallState = dataItem
            dataModel.addItemWithRefReuse(masterPage, DATA_TYPES.data, MASTER_PAGE_ID, MASTER_PAGE_ID)

            // TODO: When merging the dm_fragmentsApps exp need to remove the data from the masterPage
            if (experimentInstance.isOpen('dm_fragments') && experimentInstance.isOpen('dm_fragmentsApps')) {
                const {fragments} = extensionAPI as FragmentsExtensionAPI
                const fragmentType = RESOURCE_TYPES.APP_STATE
                const fragmentInstance = `${RESOURCE_TYPES.APP_STATE}Singleton`
                const fragmentIndex = {name: fragmentInstance, type: RESOURCE_TYPES.APP_STATE}
                fragments.addFragmentIndex(fragmentIndex)
                fragments.add(dataItem, DATA_TYPES.data, fragmentType, fragmentInstance)
            }
        }

        const migrateAppsStateToFragment = () => {
            const oldState = dal.get(getStateMapPointerFromMasterPage())
            addStateToDataModel(oldState)
        }

        const getStateMap = (): AppsInstallStateMap => {
            const stateMapPointer = getStateMapPointer(shouldGetFromFragment(experimentInstance))
            let stateMap = dal.get(stateMapPointer)
            if (!stateMap) {
                stateMap = {}
                addStateToDataModel({})
            }
            return stateMap
        }

        const setStateMap = (newStateMap: AppsInstallStateMap): void => {
            const stateMapPointer = getStateMapPointer(shouldGetFromFragment(experimentInstance))
            dal.set(stateMapPointer, newStateMap)
            if (experimentInstance.isOpen('dm_fragmentsApps') && !experimentInstance.isOpen('dm_fragmentsGetApps')) {
                //If dm_fragmentsApps is open and the dm_fragmentsGetApps is not open yet, we need to update the data in the AppState Fragment also
                const fragmentStateMapPointer = getStateMapPointerFromFragment()
                if (!dal.get(fragmentStateMapPointer)) {
                    migrateAppsStateToFragment()
                }
                dal.set(fragmentStateMapPointer, newStateMap)
            } else if (
                experimentInstance.isOpen('dm_fragmentsApps') &&
                experimentInstance.isOpen('dm_fragmentsGetApps')
            ) {
                //If dm_fragmentsApps is open and the dm_fragmentsGetApps is also open, we need to update the data in the masterPage for case we will close the exp
                const masterPageStateMapPointer: Pointer = getStateMapPointerFromMasterPage()
                dal.set(masterPageStateMapPointer, newStateMap)
            }
        }

        const getState = (appDefId: AppDefinitionId): AppInstallState | undefined => {
            if (!appDefId) {
                return
            }
            const stateMap = getStateMap()
            return stateMap[appDefId]
        }

        const setState = (appDefId: AppDefinitionId, newState: AppInstallState): void => {
            if (newState.version) {
                newState.version = String(newState.version)
            }
            const stateMap = deepClone(getStateMap())
            stateMap[appDefId] = newState
            setStateMap(stateMap)
            eventEmitter.emit(EVENTS.APPS_INSTALL_STATE.UPDATE)
        }

        const getAppInstallStatus = (appDefId: AppDefinitionId): string | undefined => {
            const state = getState(appDefId)
            return state?.status
        }

        const setAppInstallState = (
            appDefId: AppDefinitionId,
            newState: AppInstallState,
            originFunc?: string,
            source?: string
        ) => {
            if (!appDefId) {
                return
            }
            const oldState = getState(appDefId)

            coreConfig.logger.interactionStarted('setAppInstallState', {
                tags: {func: originFunc, source},
                extras: {appDefId, oldState, newState}
            })

            const state = _.merge({}, oldState, newState)
            if (!state?.version) {
                delete state?.version
            }
            setState(appDefId, state)
            coreConfig.logger.interactionEnded('setAppInstallState')
        }

        const isAppInstalled = (appDefId: AppDefinitionId | undefined) => {
            if (!appDefId) {
                return false
            }
            const state = getState(appDefId)
            return state?.status === INSTALLED
        }

        const isAppRemoved = (appDefId: AppDefinitionId) => {
            if (!appDefId) {
                return false
            }
            const state = getState(appDefId)
            return state?.status === REMOVED
        }

        const getInstalledApps = () => {
            const stateMap = getStateMap()
            return installedAppsFromStateMap(stateMap)
        }

        const getAppInstallStateDifference = (appData: any, action: string) => {
            if (!appData) {
                return
            }
            const appInstallStatus = getAppInstallStatus(appData.appDefinitionId)
            const isAppStatusActive = appInstallStatus === INSTALLED
            const isAppActive = action === 'add'
            if (isAppActive !== isAppStatusActive) {
                return {action, appInstallStatus}
            }
        }

        const getAppsInstallStateDifference = (
            clientSpecMap: ClientSpecMap,
            actions: {type: string; applicationId: ApplicationId}[]
        ) => {
            return _.reduce(
                actions,
                (
                    result: Record<AppDefinitionId, {action: string; appInstallStatus: string | undefined}>,
                    action: {type: string; applicationId: ApplicationId}
                ) => {
                    if (['remove', 'add'].includes(action.type)) {
                        const appData: any = clientSpecMap[action.applicationId]
                        const difference = getAppInstallStateDifference(appData, action.type)
                        if (difference) {
                            result[appData.appDefinitionId] = difference
                        }
                    }
                    return result
                },
                {}
            )
        }

        const reportStateDifferenceByActions = (
            clientSpecMap: ClientSpecMap,
            actions: {type: string; applicationId: ApplicationId}[],
            source: string
        ) => {
            const appDefIdsWithDifference = getAppsInstallStateDifference(clientSpecMap, actions)
            if (_.isEmpty(appDefIdsWithDifference)) {
                return
            }
            coreConfig.logger.interactionStarted('compareInstallStatus', {
                extras: {source, appDefIds: appDefIdsWithDifference},
                tags: {valuesEqual: false}
            })
        }

        const getInstallStateByAppData = (app: ClientSpecMapEntry, appsWithComps: Set<AppDefinitionId> = new Set()) => {
            const routerConfigMap = dal.get(pointers.routers.getRoutersConfigMapPointer())
            const appDefinitionId = _.get(app, ['appDefinitionId'])
            const isAppPending = tpaUtils.isPendingAppData(app as AppData)
            if (appDefinitionId && tpaUtils.isEditorOrHybridApp(app) && !isAppPending) {
                const hasCompsOnPage = appsWithComps.has(appDefinitionId)
                const hasRouter = tpaUtils.appHasRouter(app, routerConfigMap)
                const isAppExcludedFromAutoRevoke = tpaUtils.isAppExcludedFromAutoRevoke(app)
                const isAppRevoked = tpaUtils.isAppRevoked(app)
                const isStudioApp = tpaUtils.isStudioApp(app)
                const installedWithoutComps =
                    !isAppRevoked &&
                    (isStudioApp || tpaUtils.isAppPlatformEditorOnly(app) || isAppExcludedFromAutoRevoke)
                const isInstalled = hasCompsOnPage || installedWithoutComps || hasRouter

                return {
                    status: isInstalled ? INSTALLED : REMOVED
                }
            }
        }

        const setAppInstallStateByAppData = (app: EditorClientSpecMapEntry) => {
            const source = 'setAppInstallStateByAppData'
            const appsInstallStateAPI = (extensionAPI as AppsInstallStateExtensionAPI).appsInstallState
            if (tpaUtils.isAppRevoked(app)) {
                appsInstallStateAPI.setAppUninstalled(app.appDefinitionId, source)
            } else if (!appsInstallStateAPI.isAppInstalled(app.appDefinitionId)) {
                appsInstallStateAPI.setAppInstalled(app.appDefinitionId, source)
            }
        }

        const setAppInstalled = (appDefId: AppDefinitionId, source?: string) =>
            setAppInstallState(appDefId, {status: INSTALLED}, 'setAppInstalled', source)

        const setAppUninstalled = (appDefId: AppDefinitionId, source?: string) =>
            setAppInstallState(appDefId, {status: REMOVED}, 'setAppUninstalled', source)

        const fixAppStatus = (appDefId: AppDefinitionId, source?: string, app?: ClientSpecMapEntry) => {
            const appData = app ?? _.find(rendererModel.getClientSpecMap(), {appDefinitionId: appDefId})
            if (!rendererModel.isAppRevoked(appData) && platform.shouldExcludeFromAutoRevoke(appData)) {
                setAppInstalled(appDefId, source ?? 'fixAppStatus')
            } else {
                setAppUninstalled(appDefId, source ?? 'fixAppStatus')
            }
        }

        const fixAppsStatus = (revisionStateMap?: AppsInstallStateMap) => {
            const appsData = rendererModel.getClientSpecMap()
            const stateMap = getStateMap()
            _.forEach(appsData, app => {
                const appDefId = _.get(app, ['appDefinitionId']) as AppDefinitionId
                const editorOrHybridApp = tpaUtils.isEditorOrHybridApp(app)
                const isAppPending = tpaUtils.isPendingAppData(app as AppData)
                const appNotPreInstalled = !_.get(app, ['preInstalled'])
                const appInstalledOnOldRevision = revisionStateMap?.[appDefId]?.status === INSTALLED
                const shouldAppUpdate =
                    !isAppPending &&
                    editorOrHybridApp &&
                    appNotPreInstalled &&
                    (!revisionStateMap || appInstalledOnOldRevision)
                const stateExist = !!stateMap[appDefId]
                if (!stateExist && shouldAppUpdate) {
                    coreConfig.logger.interactionStarted('fixAppStatus', {
                        extras: {appDefId}
                    })
                    fixAppStatus(appDefId, 'fixAppsStatus - load', app)
                }
            })
        }

        const asyncFixAppsStatus = async () => {
            const branchId = branches.getBranchId()
            const restoreRevisionInfo = documentServicesModel.getRestoreRevisionInfo()
            const revisionStateMap = await branches.getBranchAppsState(branchId, String(restoreRevisionInfo.revision))
            fixAppsStatus(revisionStateMap as AppsInstallStateMap)
        }

        const getDiffBetweenSpecAndAppState = () => {
            const stateMap: AppsInstallStateMap = getStateMap()
            const filteredStateMap = Object.fromEntries(
                Object.entries(stateMap).filter(([key]) => !['dataBinding', 'wix-code'].includes(key))
            )
            const clientSpecMap: ClientSpecMap = dal.get(pointers.getPointer('clientSpecMap', 'rendererModel'))
            const appsOnClientSpecMapThatAreNotInStateMap = Object.values(clientSpecMap)
                .filter((app: any) => tpaUtils.isEditorOrHybridApp(app))
                .reduce((result: string[], app: any) => {
                    if (app.appDefinitionId) {
                        const appInstalledOnClientSpecMap = !app.permissions?.revoked
                        const appInstalledOnStateMap = filteredStateMap[app.appDefinitionId]?.status === INSTALLED
                        if (appInstalledOnClientSpecMap && !appInstalledOnStateMap) {
                            result.push(app.appDefinitionId)
                        }
                    }
                    return result
                }, [])
            return appsOnClientSpecMapThatAreNotInStateMap
        }

        return {
            appsInstallState: {
                isAppInstalled: (appDefId: AppDefinitionId) => isAppInstalled(appDefId),
                isAppRemoved: (appDefId: AppDefinitionId) => isAppRemoved(appDefId),
                setAppInstalled,
                setAppUninstalled,
                getAppInstallState: getState,
                getAllAppsInstallStatus: getStateMap,
                getInstallStateByAppData,
                getInstalledApps,
                reportStateDifferenceByActions: (
                    clientSpecMap: ClientSpecMap,
                    actions: {type: string; applicationId: ApplicationId}[],
                    source: string
                ) => reportStateDifferenceByActions(clientSpecMap, actions, source),
                setAppInstallStateByAppData,
                addStateToDataModel,
                fixAppStatus,
                fixAppsStatus,
                asyncFixAppsStatus,
                getDiffBetweenSpecAndAppState,
                subscribeToStateChanges: (listener: AppStateChangedCallback) => {
                    if (_.isFunction(listener)) {
                        stateChangeListeners.push(listener)
                    }
                },
                getStateMapPointerFromMasterPage,
                migrateAppsStateToFragment
            }
        }
    }

    const updateStateChanges = (newPendingAppsState: AppsInstallStateMap) => {
        const appsToRevoke: any = []
        const appsToGrant: any = []
        const appsToKeepRemoved: any = []
        if (localAppsInstallState && newPendingAppsState && !_.isEqual(localAppsInstallState, newPendingAppsState)) {
            const appsStatesKeys = new Set([...Object.keys(newPendingAppsState), ...Object.keys(localAppsInstallState)])
            appsStatesKeys.forEach(appDefId => {
                const currentStatus = localAppsInstallState?.[appDefId]?.status
                const newStatus = newPendingAppsState?.[appDefId]?.status
                if (currentStatus !== INSTALLED && newStatus === INSTALLED) {
                    appsToGrant.push(appDefId)
                } else if (currentStatus === INSTALLED && newStatus !== INSTALLED) {
                    appsToRevoke.push(appDefId)
                } else if (currentStatus === REMOVED && !newStatus) {
                    appsToKeepRemoved.push(appDefId)
                }
            })
            if (!_.isEmpty(appsToRevoke) || !_.isEmpty(appsToGrant) || !_.isEmpty(appsToKeepRemoved)) {
                stateChangeListeners.forEach(listener => listener(appsToGrant, appsToRevoke, appsToKeepRemoved))
            }
            localAppsInstallState = newPendingAppsState
        }
    }

    const createPostTransactionOperations = () => ({
        appsInstallState: (documentTransaction: Transaction) => {
            const pendingAppsChanges = documentTransaction.items.filter(e => e?.key?.id === APPS_INSTALL_STATE_ID)

            if (!_.isEmpty(pendingAppsChanges)) {
                pendingAppsChanges.forEach(e => {
                    const newStateMap = e.value?.stateMap
                    updateStateChanges(newStateMap)
                })
            }
        }
    })

    const initialize = async ({dal, extensionAPI}: InitializeExtArgs) => {
        const {appsInstallState} = extensionAPI as AppsInstallStateExtensionAPI

        stateChangeListeners.push(
            (installed: AppDefinitionId[], removed: AppDefinitionId[], appsToKeep: AppDefinitionId[] = []) => {
                const newStateMap = dal.get(getStateMapPointer(shouldGetFromFragment(experimentInstance)))
                ;[...removed, ...appsToKeep].forEach((appDefId: string) => {
                    const newState = newStateMap[appDefId]
                    if (_.isNil(newState)) {
                        appsInstallState.fixAppStatus(appDefId, 'undo')
                    }
                })
            }
        )

        localAppsInstallState = appsInstallState.getAllAppsInstallStatus()
    }

    return {
        name: 'appsInstallState',
        createExtensionAPI,
        EVENTS,
        createPostTransactionOperations,
        initialize,
        createPointersMethods: () => ({
            appsInstallState: {
                getStateMapPointer: () => getStateMapPointer(shouldGetFromFragment(experimentInstance))
            }
        }),
        dependencies: new Set(['dataModel'])
    }
}

export interface AppsInstallStateExtAPI extends ExtensionAPI {
    appsInstallState: AppsInstallStateAPI
}

export {createExtension, installedAppsFromStateMap, APPS_INSTALL_STATE_ID, APP_STATUS}
